import { makeStyles } from "@fluentui/react-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import ToolForm from "../components/tool/ToolForm";
import {
  useCreateToolDraft,
  useGetDocumentFields,
  useGetLinkFields,
} from "../hooks/useTools";
import { ClientRoutes } from "../models/ClientRoutes";
import { CreateToolRequest, ToolTypes } from "../models/Tool";
import { validateFormFieldsForRequest } from "../services/FieldService";

const useClasses = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
  },
});

export const CreateToolPage: React.FC = () => {
  const { t } = useTranslation();
  const classes = useClasses();
  const { mutateAsync: createTool, isPending } = useCreateToolDraft();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const toolTypeFromParam: string | null = searchParams.get("toolType");

  let defaultToolType: ToolTypes | undefined = undefined;

  if (toolTypeFromParam === "Document") {
    defaultToolType = ToolTypes.Document;
  } else if (toolTypeFromParam === "Link") {
    defaultToolType = ToolTypes.Link;
  }

  const [selectedToolType, setSelectedToolType] = useState<
    ToolTypes | undefined
  >(defaultToolType);

  const { data: linkFields, isLoading: linkFieldsLoading } = useGetLinkFields();
  const { data: documentFields, isLoading: documentFieldsLoading } =
    useGetDocumentFields();

  const viewDefinition =
    selectedToolType === ToolTypes.Document ? documentFields : linkFields;
  const viewDefinitionLoading =
    selectedToolType === ToolTypes.Document
      ? documentFieldsLoading
      : linkFieldsLoading;

  async function onFormSubmit(
    formData: CreateToolRequest,
    options?: { publishAfterSave?: boolean }
  ) {
    if (!viewDefinition) {
      return;
    }
    const requestFields = validateFormFieldsForRequest(
      formData.fieldsWithValues,
      viewDefinition.fields
    );

    const formDataToSend = new FormData();

    formDataToSend.append("Title", formData.title);
    if (formData.toolType) {
      formDataToSend.append("ToolType", formData.toolType);
    }
    if (formData.file) {
      formDataToSend.append("File", formData.file);
    }
    if (formData.fileName) {
      formDataToSend.append("FileName", formData.fileName);
    }
    if (formData.linkUrl) {
      formDataToSend.append("LinkUrl", formData.linkUrl);
    }
    formDataToSend.append("AdminDisplayName", formData.adminDisplayName || "");
    if (requestFields) {
      formDataToSend.append("FieldsWithValues", JSON.stringify(requestFields));
    }

    const createdTool = await createTool(formDataToSend);
    navigate(`${ClientRoutes.ToolsDrafts}/${createdTool.versionId}`, {
      state: { openPublishOnLoad: options?.publishAfterSave === true },
    });
  }

  return (
    <div className={classes.root}>
      <h1>{t("createTool")}</h1>

      <ToolForm
        isLoading={isPending || viewDefinitionLoading}
        onSubmit={onFormSubmit}
        toolFields={viewDefinition?.fields}
        isRefetching={isPending}
        defaultToolType={defaultToolType}
        onToolTypeChange={setSelectedToolType}
      />
    </div>
  );
};

export default CreateToolPage;
